import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Zap, Globe, Award, TrendingUp, Heart, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/20 to-black"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <Navigation />

      <div className="relative z-10 pt-24">
        {/* Hero Section */}
        <div className="py-32">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-6xl md:text-7xl font-black mb-6">
                Building the Future of
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  AI Commerce
                </span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                NEXUS is the world's most advanced AI-powered marketplace, connecting creators, 
                vendors, and innovators to build the next generation of intelligent products.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-black mb-6">Our Mission</h2>
                <p className="text-lg text-gray-400 leading-relaxed mb-6">
                  We're democratizing access to AI technology by creating a vibrant marketplace 
                  where innovation thrives. Our platform empowers creators to monetize their AI 
                  tools while providing businesses with cutting-edge solutions.
                </p>
                <p className="text-lg text-gray-400 leading-relaxed">
                  With over 268+ AI tools, real-time auctions, and seamless payment processing, 
                  we're building the infrastructure for the AI economy.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-6"
              >
                {[
                  { icon: Users, value: '50K+', label: 'Active Users' },
                  { icon: Zap, value: '268+', label: 'AI Tools' },
                  { icon: Globe, value: '120+', label: 'Countries' },
                  { icon: Award, value: '99.9%', label: 'Uptime' }
                ].map((stat, idx) => (
                  <div key={idx} className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 text-center">
                    <stat.icon className="w-8 h-8 mx-auto mb-3 text-cyan-400" />
                    <div className="text-3xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-black mb-6">Our Values</h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Principles that guide everything we do
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: 'Trust & Security',
                  desc: 'Enterprise-grade security with Firebase and Stripe integration. Your data is always protected.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: Heart,
                  title: 'Creator First',
                  desc: 'We empower creators with fair monetization, powerful tools, and a supportive community.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: Target,
                  title: 'Innovation',
                  desc: 'Powered by Gemini 2.5 Flash, Claude Sonnet 4, and cutting-edge AI technologies.',
                  gradient: 'from-teal-500 to-cyan-500'
                }
              ].map((value, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.2 }}
                  className="p-8 rounded-3xl bg-zinc-900/50 border border-zinc-800 hover:border-purple-500/50 transition-all"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${value.gradient} p-4 mb-6`}>
                    <value.icon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-black mb-6">Built by AI Experts</h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Our team combines decades of experience in AI, marketplace design, and developer tools
              </p>
            </motion.div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                'AI Engineering',
                'Product Design',
                'Marketplace Operations',
                'Developer Relations'
              ].map((dept, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-2xl bg-gradient-to-br from-zinc-900/80 to-black border border-zinc-800 text-center"
                >
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-purple-400" />
                  <h3 className="text-lg font-bold">{dept}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-32">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600 p-12 md:p-20 text-center"
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-black mb-6">
                  Join the AI Revolution
                </h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                  Be part of the community building the future of AI commerce
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/register')}
                    className="bg-black hover:bg-zinc-900 text-white px-10 py-6 text-lg rounded-full"
                  >
                    Start Building
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => navigate('/features')}
                    className="bg-white/10 hover:bg-white/20 text-white px-10 py-6 text-lg rounded-full border border-white/20"
                  >
                    Explore Features
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}